// Direct client-side PDF generation for the "Print" button — used only on the browser/OS
// combinations where the native print dialog (window.print()) is known broken; see
// shouldUsePdfFallback() in TopBar.tsx for which those are and why.
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import QRCode from 'qrcode'
import sitesData from '../data/sites.json'
import reuseSitesData from '../data/reuseSites.json'
import { SITE_CATEGORIES, SITE_CATEGORY_LABELS, SITE_CATEGORY_LABELS_SHORT, REUSE_NETWORK } from '../constants/mapConfig'
import { INGLIS_BLUE } from '../constants/colors'
import type { ReuseSite, Site, SitesData } from '../types'

const reuseSites = reuseSitesData as ReuseSite[]

function sortByName<T extends { name: string }>(list: T[]): T[] {
  return [...list].sort((a, b) => a.name.localeCompare(b.name))
}

function hexToRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}

function siteRows(sites: Site[]) {
  return sortByName(sites).map(site => [
    site.name,
    `${site.org}\n${site.address}`,
    SITE_CATEGORY_LABELS_SHORT[site.category],
    site.audiences.family?.headline ?? '',
    [site.phone || site.contact || '', site.url ? site.url.replace(/^https?:\/\//, '') : ''].filter(Boolean).join('\n'),
  ])
}

const HEAD = [['Site', 'Org & Address', 'Category', "What you'll find", 'Contact']]
const COLUMN_STYLES = {
  0: { cellWidth: 32 }, 1: { cellWidth: 65 }, 2: { cellWidth: 26 }, 3: { cellWidth: 70 }, 4: { cellWidth: 45 },
}

/** Builds and downloads the site-directory PDF directly — no print dialog involved. */
export async function downloadSiteDirectoryPdf() {
  const allSites = (sitesData as SitesData).sites
  const [r, g, b] = hexToRgb(INGLIS_BLUE)

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'letter' })
  let cursorY = 14

  doc.setFontSize(16)
  doc.setTextColor(r, g, b)
  doc.setFont('helvetica', 'bold')
  doc.text('Inglis Enabled Home Showcase — Site Directory', 14, cursorY)
  cursorY += 6

  doc.setFontSize(9)
  doc.setTextColor(110, 120, 140)
  doc.setFont('helvetica', 'normal')
  doc.text('inglis.org · Assistive Technology & Accessible Housing across Pennsylvania', 14, cursorY)
  cursorY += 6

  // Sorted by the same full label PrintSiteList.tsx sorts by (not the short label this function
  // displays as its own heading) — HOUSING's short label "Housing" and full label
  // "Tech-Integrated Accessible Housing" sort to very different positions, and the two print
  // paths (browser print vs. this PDF fallback) need to agree on section order even though they
  // deliberately show different-length heading text.
  const sortedCategories = [...SITE_CATEGORIES].sort((a, b) => SITE_CATEGORY_LABELS[a].localeCompare(SITE_CATEGORY_LABELS[b]))

  for (const category of sortedCategories) {
    const categorySites = allSites.filter(s => s.category === category)
    if (categorySites.length === 0) continue

    doc.setFontSize(10)
    doc.setTextColor(110, 120, 140)
    doc.setFont('helvetica', 'bold')
    doc.text(SITE_CATEGORY_LABELS_SHORT[category].toUpperCase(), 14, cursorY)
    cursorY += 3

    autoTable(doc, {
      startY: cursorY,
      head: HEAD,
      body: siteRows(categorySites),
      styles: { fontSize: 7.5, cellPadding: 1.5, valign: 'top' },
      headStyles: { fillColor: [247, 249, 252], textColor: [111, 124, 140], fontStyle: 'bold' },
      columnStyles: COLUMN_STYLES,
      margin: { left: 14, right: 14 },
    })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cursorY = (doc as any).lastAutoTable.finalY + 8
  }

  doc.setFontSize(10)
  doc.setTextColor(110, 120, 140)
  doc.setFont('helvetica', 'bold')
  doc.text('STATEWIDE NETWORK', 14, cursorY)
  cursorY += 3

  // Generated up front (not inside didDrawCell below) since QRCode.toDataURL is async and
  // autoTable's draw hooks run synchronously mid-render — there's no way to await inside them.
  let qrDataUrl: string | null = null
  try {
    qrDataUrl = await QRCode.toDataURL(REUSE_NETWORK.mapUrl, { width: 300, margin: 1 })
  } catch {
    // QR code is a nice-to-have on the printed page; failing to generate it shouldn't block the
    // rest of the directory from downloading.
  }

  const QR_SIZE = 16
  autoTable(doc, {
    startY: cursorY,
    head: [['Network', 'Details', '', 'Access']],
    body: [[
      REUSE_NETWORK.name,
      { content: `${reuseSites.length} independently-run equipment-reuse programs across Pennsylvania — hospitals, Lions Clubs, churches, and community groups that redistribute mobility equipment, hearing aids, and other assistive devices, often at low or no cost.`, colSpan: 2 },
      'Scan for the live interactive map',
    ]],
    styles: { fontSize: 7.5, cellPadding: 1.5, valign: 'top' },
    headStyles: { fillColor: [247, 249, 252], textColor: [111, 124, 140], fontStyle: 'bold' },
    bodyStyles: { fillColor: [247, 249, 252] },
    margin: { left: 14, right: 14 },
    // Access column needs enough width to hold both its own text and the QR_SIZE-wide image
    // drawn into its right edge below (autoTable auto-sizes narrower than that from content
    // alone, since "Scan for the live interactive map" alone doesn't need 40mm).
    columnStyles: { 3: { cellWidth: 40 } },
    // Draws the QR code inside the "Access" cell's own rendered bounds — autoTable computes
    // those from this row's real height (which grows with the Details cell's wrapped text), so
    // this stays correctly positioned regardless of how long that description ends up being,
    // unlike deriving a guessed offset from the table's finalY after the fact.
    didDrawCell: data => {
      if (!qrDataUrl || data.section !== 'body' || data.column.index !== 3) return
      const { cell } = data
      const x = cell.x + cell.width - QR_SIZE - cell.padding('right')
      const y = cell.y + cell.padding('top')
      doc.addImage(qrDataUrl, 'PNG', x, y, QR_SIZE, QR_SIZE)
    },
  })
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  cursorY = (doc as any).lastAutoTable.finalY + 8

  function reuseSubTable(label: string, sites: ReuseSite[]) {
    doc.setFontSize(9)
    doc.setTextColor(110, 120, 140)
    doc.setFont('helvetica', 'bold')
    doc.text(`${label} (${sites.length})`, 14, cursorY)
    cursorY += 3

    autoTable(doc, {
      startY: cursorY,
      head: [['Site', 'Contact']],
      body: sortByName(sites).map(site => [site.name, site.phone || site.email || '']),
      styles: { fontSize: 7, cellPadding: 1.2, valign: 'top' },
      headStyles: { fillColor: [247, 249, 252], textColor: [111, 124, 140], fontStyle: 'bold' },
      columnStyles: { 0: { cellWidth: 60 } },
      margin: { left: 14, right: 14 },
    })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cursorY = (doc as any).lastAutoTable.finalY + 8
  }

  reuseSubTable('Reuse Programs', sortByName(reuseSites))

  doc.save('inglis-site-directory.pdf')
}
